/**
 * HTTP client for the Quick Review board APIs via the shared api-client.
 *
 * The board surfaces PA orders whose monitored fields drifted between the
 * value the order was worked in and the newly ingested value.
 *
 * @module api/services/quickReviewService
 */

import { api } from "@risalabs_frontend_org/api-client";
import {
  QuickReviewCountResponse,
  QuickReviewDecisionRequest,
  QuickReviewDecisionResult,
  QuickReviewDetailRequest,
  QuickReviewDetailResponse,
  QuickReviewListRequest,
  QuickReviewListResponse,
} from "../../data-model/quickReviewModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";

/**
 * Service for the Quick Review board APIs.
 *
 * @example
 * await QuickReviewService.list({ page: 0, hits_per_page: 25 });
 */
export class QuickReviewService {
  /**
   * Fetches one page of pending Quick Review rows for the caller's org.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Page number (0-indexed) and results per page.
   * @param signal - Optional abort signal for in-flight cancellation.
   * @returns Board cards for the requested page plus board-wide totals.
   * @throws Error when auth is missing or the HTTP request fails.
   */
  static async list(
    payload: QuickReviewListRequest,
    signal?: AbortSignal,
  ): Promise<QuickReviewListResponse> {
    return api.post<QuickReviewListRequest, QuickReviewListResponse>(
      API_ENDPOINTS.QUICK_REVIEW_LIST,
      payload,
      signal,
    );
  }

  /**
   * Fetches the total number of pending Quick Review rows for the caller's org.
   *
   * Bearer token is injected by {@link api.get}.
   *
   * @param signal - Optional abort signal for in-flight cancellation.
   * @returns Board-wide pending row count.
   * @throws Error when auth is missing or the HTTP request fails.
   */
  static async count(signal?: AbortSignal): Promise<QuickReviewCountResponse> {
    return api.get<QuickReviewCountResponse>(
      API_ENDPOINTS.QUICK_REVIEW_COUNT,
      signal,
    );
  }

  /**
   * Opens one boarded order, comparing every monitored field's worked value
   * against the newly ingested value.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - PA order id to open.
   * @param signal - Optional abort signal for in-flight cancellation.
   * @returns All monitored fields, worked value against new.
   * @throws Error when auth is missing or the HTTP request fails.
   */
  static async detail(
    payload: QuickReviewDetailRequest,
    signal?: AbortSignal,
  ): Promise<QuickReviewDetailResponse> {
    return api.post<QuickReviewDetailRequest, QuickReviewDetailResponse>(
      API_ENDPOINTS.QUICK_REVIEW_DETAIL,
      payload,
      signal,
    );
  }

  /**
   * Applies a reviewer's decision to a boarded order — reprocess it through
   * the pipeline or accept it as-is.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Order id, decision, and optional record of what the
   * reviewer saw.
   * @param signal - Optional abort signal for in-flight cancellation.
   * @returns Board audit fields as they stand once the decision is applied.
   * @throws Error when auth is missing or the HTTP request fails.
   */
  static async decision(
    payload: QuickReviewDecisionRequest,
    signal?: AbortSignal,
  ): Promise<QuickReviewDecisionResult> {
    return api.post<QuickReviewDecisionRequest, QuickReviewDecisionResult>(
      API_ENDPOINTS.QUICK_REVIEW_DECISION,
      payload,
      signal,
    );
  }
}
